import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import * as Store from '@apply/store';
import { alertModal, loginAlertModal, EsignNoticeModal, FloodReportEsignModal, Font, confirmModal } from '@apply/ui';
import { Header } from '../Header';
import { useAuthentication } from '../LoginPage';
import DataGrid, { Column, Selection, Paging, Pager } from 'devextreme-react/data-grid';
import LoadPanel from 'devextreme-react/load-panel';
import { FeatureType, NoticeType } from '@apply/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faPencil } from '@fortawesome/free-solid-svg-icons';
import { faEye, faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { HomePageLink } from '../HomePageLink';

export interface NoticesPageProps {
    actions?: Actions;
    notices?: Store.UserStore.Types.UserNoticeLean[];
}

export interface Actions {
    getUserNotices?: () => Promise<void>;
}

const formatDate = (dt) => {
    if (!dt || !dt._isValid) {
        return null;
    }

    const formattedDate = moment.tz(dt, 'America/Chicago');
    return formattedDate.format('M/D/YYYY hh:mm');
};

const getUserNotice = async (id) => await Store.CrossStore.getUserNotice(id);
const signUserNotice = async (signNotice: Store.UserStore.Types.SignUserNotice) =>
    await Store.CrossStore.signUserNotice(signNotice);
const setUserNoticeRead = async (id: number) => await Store.CrossStore.setUserNoticeRead(id);
const deleteUserNotice = async (id: number) => await Store.CrossStore.deleteUserNotice(id);
const uploadSignedFloodReport = async (noticeId, file) => await Store.CrossStore.uploadSignedFloodReport(noticeId, file);

export const NoticesPage: React.FC<NoticesPageProps> = ({ actions, notices }) => {
    const { isRegisteredUser, authentication, logout, isFeatureEnabled } = useAuthentication();
    const [loadError, setLoadError] = useState<string>('');

    const [userNotice, set_userNotice] = useState<Store.UserStore.Types.UserNotice>(null);
    const [viewModalOpen, set_viewModalOpen] = useState<boolean>(false);
    const [floodReportSignOpen, set_floodReportSignOpen] = useState<boolean>(false);

    const [loadingOpen, set_loadingOpen] = useState<boolean>(false);
    const [loadingMessage] = useState<string>('Loading...');

    const allowedPageSizes = [15, 25, 50];
    const defaultPageSize = allowedPageSizes[1];

    const thisLocation = useLocation();
    const navigate = useNavigate();

    const styles = {
        noticesContainer: {
            width: '100%' as const,
        },
        verticalPadding: {
            paddingTop: 50,
            paddingBottom: 50,
        },
        button: {
            margin: '0 5px 0 0',
            width: '40px',
            padding: '5px',
            borderColor: '#2c5591',
            backgroundColor: 'white',
            color: '#2c5591',
        },
    };

    const loadGrid = async () => {
        await actions
            .getUserNotices()
            .then(() => {
                set_loadingOpen(false);
            })
            .catch((err) => {
                setLoadError(err);
                set_loadingOpen(false);
            });
    };

    useEffect(() => {
        if (authentication?.user?.userId && actions?.getUserNotices) {
            set_loadingOpen(true);
            setLoadError('');
            loadGrid();
        }
    }, [authentication?.user?.userId, actions]);

    useEffect(() => {
        if (loadError) {
            if (String(loadError).includes('401 - User not Authorized.')) {
                loginAlertModal({
                    onConfirm: () => {
                        setLoadError(undefined);
                        logout();
                        navigate(`/login?redirectTo=${thisLocation.pathname}`);
                    },
                });
            } else {
                alertModal({
                    message: 'An error has occurred while communicating with our server.  Please try your operation again.',
                    onConfirm: () => {
                        setLoadError(undefined);
                    },
                });
            }
        }
    }, [navigate, loadError, logout, thisLocation.pathname]);

    if (!isRegisteredUser || !authentication?.username) {
        //set_loadingOpen(true);
        return (
            <LoadPanel
                hideOnOutsideClick={false}
                visible={true}
                message={'Loading...'}
            />
        );
    }

    const onClickViewNotice = async (id: number) => {
        try {
            set_loadingOpen(true);
            const notice = await getUserNotice(id);
            set_userNotice(notice);
            if (notice.noticeTypeId === NoticeType.FloodNotice) {
                set_floodReportSignOpen(true);
            } else {
                if (notice.noticeFile) {
                    set_viewModalOpen(true);
                }
            }
        } finally {
            set_loadingOpen(false);
        }
    };

    const onConfirmESign = async (font: Font) => {
        set_viewModalOpen(false);
        if (font) {
            const model = {
                noticeId: userNotice.id,
                fontId: parseInt(font.id),
            };
            await signUserNotice(model);
            await loadGrid();
            alertModal({
                title: 'Success',
                message:
                    'Your form was signed successfully. A regenerated document will be available for download in a few minutes.',
                onConfirm: () => {
                    setLoadError(undefined);
                },
            });
        }
    };

    const onFloodReportSign = async (fileToUpload) => {
        set_floodReportSignOpen(false);

        if (fileToUpload) {
            //set_loadingMessage(`Uploading flood report...`);
            const result = await uploadSignedFloodReport(userNotice.id, fileToUpload);
            if (result) {
                await loadGrid();
                alertModal({
                    title: 'Success',
                    message: 'Your document was submitted successfully.',
                    onConfirm: () => {
                        setLoadError(undefined);
                    },
                });
            } else {
                alertModal({
                    message: 'An error has occurred while communicating with our server.  Please try your operation again.',
                    onConfirm: () => {
                        setLoadError(undefined);
                    },
                });
            }
        }
    };

    const onCloseESign = async () => {
        set_viewModalOpen(false);
        if (!userNotice.isRead) {
            await setUserNoticeRead(userNotice.id);
            await loadGrid();
        }
    };

    const onDateRender = (e) => {
        return formatDate(e.value);
    };

    const onTypeRender = (e) => {
        switch (e.value) {
            case Store.UserStore.Types.NoticeType.AdverseAction:
                return 'Adverse Action';
            case Store.UserStore.Types.NoticeType.AppraisalNotice:
                return 'Appraisal Notice';
            case Store.UserStore.Types.NoticeType.AppraisalReport:
                return 'Appraisal Report';
            case Store.UserStore.Types.NoticeType.FloodNotice:
                return 'Flood Notice';
            case Store.UserStore.Types.NoticeType.IncompleteFile:
                return 'Incomplete File';
            default:
                return 'Unknown';
        }
    };

    const deleteNoticeAndReload = async (noticeId) => {
        await deleteUserNotice(noticeId);
        await loadGrid();
    };

    const onDelete = async (event, noticeId) => {
        confirmModal({
            message:
                'Are you sure you want to delete this notification? The noticification will no longer be visible in the grid.',
            title: 'Delete Confirmation',
            onConfirm: () => {
                deleteNoticeAndReload(noticeId);
            },
        });
    };

    const onCellDblClick = (e) => {
        onClickViewNotice(e.data.id);
    };

    if (!isFeatureEnabled(FeatureType.Notices)) {
        window.location.href = '/home';
    }

    const onSignRender = (e) => {
        return (
            <div>
                {e.data.eSignTypeId !== Store.UserStore.Types.ESignType.NotRequired && (
                    <button
                        className='button'
                        // eslint-disable-next-line
                        onClick={(event) => onClickViewNotice(e.data.id)}
                        title={
                            e.data.isManual
                                ? 'View Unavailable. Download only.'
                                : e.data.eSignDate?._isValid
                                  ? 'View Notice'
                                  : 'Sign Notice'
                        }
                        style={styles.button}
                        disabled={e.data.isManual}
                    >
                        {e.data.eSignDate?._isValid ? (
                            <FontAwesomeIcon
                                icon={faEye}
                                aria-hidden='true'
                            />
                        ) : (
                            <FontAwesomeIcon
                                icon={faPencil}
                                aria-hidden='true'
                            />
                        )}
                    </button>
                )}
            </div>
        );
    };

    const onDeleteRender = (e) => {
        return (
            <button
                className='button'
                onClick={(event) => onDelete(event, e.data.id)}
                title='Delete Notice'
                style={styles.button}
                disabled={!e.data.canDelete}
            >
                <FontAwesomeIcon
                    icon={faTrashCan}
                    aria-hidden='true'
                />
            </button>
        );
    };

    const markAsRead = async (notice) => {
        if (notice.noticeTypeId === NoticeType.FloodNotice) {
            //Can only be marked as read if they signed it.
            return;
        }
        if (!notice.isRead) {
            await setUserNoticeRead(notice.id);
            await loadGrid();
        }
    };

    const onNoticeFilesRender = (e) => {
        const f = e.data?.signedFile ?? e.data?.noticeFile;
        if (!f) {
            return 'Not Avail.';
        } else {
            return (
                <a
                    href={f.url}
                    target='_blank'
                    title={f.address ?? f.name}
                    // eslint-disable-next-line
                    onClick={(event) => markAsRead(e.data)}
                >
                    <FontAwesomeIcon
                        icon={faDownload}
                        aria-hidden='true'
                    />
                </a>
            );
        }
    };

    const onAddressCellRender = (e) => {
        if (!e.value) {
            return null;
        }
        return e.value.map((a, index) => {
            return (
                <div
                    key={'addr' + index}
                    style={{ marginBottom: '5px' }}
                >
                    {a.street1} {a.street2}
                    <br />
                    {a.city}, {a.state} {a.zipCode}
                </div>
            );
        });
    };

    const onRowPrepared = (e) => {
        if (e.rowType !== 'data') {
            return;
        }

        if (e.data.eSignTypeId === Store.UserStore.Types.ESignType.Required && !e.data.eSignDate?._isValid) {
            const classNames = e.rowElement.className.replace('dx-row-alt', '');
            e.rowElement.className = classNames + ' dx_portfolio_highlight1';
        }
    };

    return (
        <div className='app-container home-page-container'>
            <Header />
            <main>
                <HomePageLink />
                <h2>My Notifications</h2>
                {!loadingOpen && notices?.length > 0 && (
                    <>
                        <div
                            className='columns small-12 large-12'
                            style={styles.noticesContainer}
                        >
                            <div
                                style={{
                                    backgroundColor: '#f9f5d5',
                                    padding: '5px',
                                    width: '100%',
                                    textAlign: 'center',
                                }}
                            >
                                Highlighted rows require signature
                            </div>
                        </div>
                        <div
                            className='columns small-12 large-12'
                            style={styles.noticesContainer}
                        >
                            <DataGrid
                                keyExpr='id'
                                id='noticeTable'
                                dataSource={notices}
                                showBorders={true}
                                showRowLines={false}
                                rowAlternationEnabled={true}
                                remoteOperations={false}
                                allowColumnResizing={true}
                                allowColumnReordering={false}
                                columnResizingMode={'nextColumn'}
                                columnMinWidth={20}
                                onCellDblClick={onCellDblClick}
                                cellHintEnabled={true}
                                onRowPrepared={onRowPrepared}
                                width={'auto'}
                            >
                                <Selection mode='single' />
                                <Column
                                    dataField='noticeTypeId'
                                    caption='Type'
                                    width={130}
                                    cellRender={onTypeRender}
                                    alignment='left'
                                />
                                <Column
                                    caption='Address'
                                    dataField='addresses'
                                    alignment='left'
                                    minWidth={130}
                                    allowSorting={false}
                                    cellRender={onAddressCellRender}
                                />
                                <Column
                                    dataField='noticeDate'
                                    caption='Date/Time'
                                    cellRender={onDateRender}
                                    width={120}
                                />
                                <Column
                                    dataField='isRead'
                                    caption='Read'
                                    width={50}
                                />
                                <Column
                                    caption='Signed'
                                    dataField='eSignName'
                                    width={130}
                                />
                                <Column
                                    caption='File'
                                    width={60}
                                    cellRender={onNoticeFilesRender}
                                    alignment='center'
                                />
                                <Column
                                    caption='Sign'
                                    cellRender={onSignRender}
                                    width={60}
                                    alignment='center'
                                />
                                <Column
                                    caption='Delete'
                                    cellRender={onDeleteRender}
                                    width={60}
                                    alignment='center'
                                />
                                <Paging defaultPageSize={defaultPageSize} />
                                <Pager
                                    showPageSizeSelector={allowedPageSizes.length > 1}
                                    allowedPageSizes={allowedPageSizes}
                                    showNavigationButtons={true}
                                    showInfo={true}
                                    visible={true}
                                />
                            </DataGrid>
                        </div>
                    </>
                )}
                {!loadingOpen && !notices?.length && (
                    <ul>
                        <li>
                            <em>No Notices at this time</em>
                        </li>
                    </ul>
                )}
            </main>
            <EsignNoticeModal
                notice={userNotice}
                isOpen={viewModalOpen}
                signatureText={userNotice?.eSignName ?? authentication.user.firstName + ' ' + authentication.user.lastName}
                onCloseCallback={onCloseESign}
                onSignCallback={onConfirmESign}
            />
            <FloodReportEsignModal
                notice={userNotice}
                isOpen={floodReportSignOpen}
                onCloseCallback={onFloodReportSign}
            />
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
        </div>
    );
};

export const mapStateToProps = (state: Store.ApplyRootStateType) => ({
    notices: state.user?.userNotices ?? [],
});

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            getUserNotices: () => dispatch(Store.CrossStore.getUserNotices()),
        },
        dispatch,
    };
    return props;
};

export default connect(mapStateToProps, mapDispatchToProps)(NoticesPage);
